/*
 * Flow-based Reversibility: teaching simulator (Case Study, HL only)
 * ------------------------------------------------------------------------------
 * WHAT IT TEACHES (very simple, broad)
 *   A flow-based model learns an exact, reversible mapping between noise and an
 *   image, so you can run it forward (noise to image) and back (image to noise)
 *   with no loss.
 *     • Forward turns the latent noise into the image.
 *     • Reverse turns the image back into exactly the same noise.
 *     • The mapping is reversible and lossless: nothing is thrown away.
 *
 * This is an ILLUSTRATION, not real machine learning. Nothing is trained, no
 * dataset is loaded, no library is used. The "model" is a genuinely invertible
 * transform on a 16x16 toy image (add a fixed key per cell, then apply a fixed
 * permutation, all mod 256), so the round trip really is bit-exact. A lossy
 * toggle shows, by contrast, what happens when a step does throw information away.
 *
 * CONTROLS
 *   • Forward (noise to image) ...... apply the reversible transform.
 *   • Reverse (image to noise) ...... apply the inverse transform.
 *   • Reset ......................... return to the starting noise.
 *   • Lossy step toggle ............. optionally corrupt the forward step to show
 *                                     what "not lossless" looks like (contrast).
 *   • Integrity panel ............... counts cells that differ from the exact
 *                                     target, i.e. an explicit "no loss" readout.
 *   • Explainer accordion ........... reversible and lossless, in brief.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── brookebit palette (shared with the other simulators) ───────────────────────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  purple:       '#7a5bbf',
  purpleSoft:   '#efe7fb',
  amberDark:    '#c9a12e',
  red:          '#c76a5a',
  redSoft:      '#f7e7e2',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

const G = 16;                 // grid is G x G cells
const CELLS = G * G;          // 256 cells
const LEN = CELLS * 3;        // RGB per cell

// Deterministic RNG so the key and permutation are fixed every run.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Fixed key (added per channel) and a fixed cell permutation. Both are exactly
// invertible mod 256, so the transform below is a true bijection.
const KEY = (() => { const r = mulberry32(20260717); const k = new Uint8Array(LEN); for (let i = 0; i < LEN; i++) k[i] = (r() * 256) | 0; return k; })();
const PERM = (() => {
  const r = mulberry32(99991);
  const p = Array.from({ length: CELLS }, (_, i) => i);
  for (let i = CELLS - 1; i > 0; i--) { const j = (r() * (i + 1)) | 0; [p[i], p[j]] = [p[j], p[i]]; }
  return p;
})();

// scramble : image -> noise    (add key, then permute cells)
function scramble(src) {
  const a = new Uint8Array(LEN);
  for (let i = 0; i < LEN; i++) a[i] = (src[i] + KEY[i]) & 255;
  const out = new Uint8Array(LEN);
  for (let i = 0; i < CELLS; i++) for (let c = 0; c < 3; c++) out[i * 3 + c] = a[PERM[i] * 3 + c];
  return out;
}
// unscramble : noise -> image  (inverse permute, then subtract key)
function unscramble(src) {
  const a = new Uint8Array(LEN);
  for (let i = 0; i < CELLS; i++) for (let c = 0; c < 3; c++) a[PERM[i] * 3 + c] = src[i * 3 + c];
  const out = new Uint8Array(LEN);
  for (let i = 0; i < LEN; i++) out[i] = (a[i] - KEY[i]) & 255;   // &255 gives correct mod for negatives
  return out;
}
// A deliberately lossy forward: recover the image, then quantise it (throwing
// away low bits). This is NOT invertible, so the round trip no longer matches.
function unscrambleLossy(src) {
  const img = unscramble(src);
  for (let i = 0; i < LEN; i++) img[i] = Math.min(255, Math.round(img[i] / 48) * 48);
  return img;
}

// ── The toy image: a 16x16 smiley ──────────────────────────────────────────────
function buildSmiley() {
  const d = new Uint8Array(LEN);
  const put = (x, y, r, g, b) => { const i = (y * G + x) * 3; d[i] = r; d[i + 1] = g; d[i + 2] = b; };
  const cxp = 7.5, cyp = 7.5;
  const eyes = new Set(['5,6', '10,6']);
  const mouth = new Set(['5,10', '10,10', '6,11', '7,11', '8,11', '9,11']);
  for (let y = 0; y < G; y++) for (let x = 0; x < G; x++) {
    const dist = Math.hypot(x - cxp, y - cyp);
    const key = `${x},${y}`;
    if (eyes.has(key)) put(x, y, 42, 36, 30);
    else if (mouth.has(key)) put(x, y, 42, 36, 30);
    else if (dist <= 6.9) put(x, y, 245, 205, 70);      // face
    else put(x, y, 150, 196, 230);                       // background
  }
  return d;
}

const IMAGE = buildSmiley();          // canonical image
const NOISE = scramble(IMAGE);        // canonical latent noise (the starting point)

// ── Draw a grid to a canvas ────────────────────────────────────────────────────
function drawGrid(canvas, data, px) {
  const size = G * px;
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  for (let y = 0; y < G; y++) for (let x = 0; x < G; x++) {
    const i = (y * G + x) * 3;
    ctx.fillStyle = `rgb(${data[i]}, ${data[i + 1]}, ${data[i + 2]})`;
    ctx.fillRect(x * px, y * px, px, px);
  }
}

function MiniGrid({ data, px = 5 }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) drawGrid(ref.current, data, px); }, [data, px]);
  return <canvas ref={ref} style={{ width: G * px, height: G * px, display: 'block', borderRadius: 6, border: `1px solid ${B.border}`, imageRendering: 'pixelated' }} />;
}

function countDiff(a, b) {
  let n = 0;
  for (let i = 0; i < CELLS; i++) {
    const o = i * 3;
    if (a[o] !== b[o] || a[o + 1] !== b[o + 1] || a[o + 2] !== b[o + 2]) n++;
  }
  return n;
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function FlowReversibilitySimulator({ onBack }) {
  const bigRef = useRef(null);

  const [data, setData] = useState(() => NOISE.slice());
  const [state, setState] = useState('noise');   // 'noise' | 'image'
  const [lossy, setLossy] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  useEffect(() => { if (bigRef.current) drawGrid(bigRef.current, data, 18); }, [data]);

  const forward = useCallback(() => {
    if (state !== 'noise') return;
    setData(lossy ? unscrambleLossy(data) : unscramble(data));
    setState('image');
  }, [state, lossy, data]);

  const reverse = useCallback(() => {
    if (state !== 'image') return;
    setData(scramble(data));
    setState('noise');
  }, [state, data]);

  const reset = useCallback(() => { setData(NOISE.slice()); setState('noise'); }, []);

  const toggleLossy = useCallback(() => {
    setLossy((v) => !v);
    setData(NOISE.slice()); setState('noise');   // reset so the contrast is clean
  }, []);

  // Integrity: how far is the current grid from the EXACT target for this state?
  const reference = state === 'noise' ? NOISE : IMAGE;
  const diff = countDiff(data, reference);
  const lossless = diff === 0;

  // ── Styles ───────────────────────────────────────────────────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '8px 1.5rem', minHeight: 60, gap: '0.75rem', flexWrap: 'wrap',
    },
    backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: B.muted, fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6 },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6 },
    lvlBadge: { fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', background: B.purpleSoft, color: B.purple, padding: '3px 7px', borderRadius: 6 },
    body: { maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: { background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: '1.25rem', marginBottom: '1rem' },
    sectionLabel: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem' },
    schematic: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', flexWrap: 'wrap' },
    endBox: { textAlign: 'center' },
    endLabel: { fontSize: '0.72rem', fontWeight: 700, color: B.muted, marginTop: 5 },
    arrows: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 92, color: B.muted },
    arrowText: { fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' },
    stage: { display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' },
    bigCanvas: { width: '100%', maxWidth: 288, aspectRatio: '1', display: 'block', borderRadius: 10, border: `1px solid ${B.border}`, imageRendering: 'pixelated' },
    stateBadge: (st) => ({ display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.04em', padding: '4px 10px', borderRadius: 6, background: st === 'noise' ? B.purpleSoft : B.greenSoft, color: st === 'noise' ? B.purple : B.greenDark }),
    btnPrimary: (on) => ({ fontFamily: font, fontSize: '0.88rem', fontWeight: 700, cursor: on ? 'pointer' : 'not-allowed', opacity: on ? 1 : 0.4, background: B.green, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 16px', display: 'inline-flex', alignItems: 'center', gap: 7 }),
    btnGhost: { fontFamily: font, fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer', background: B.bg, color: B.text, border: `1.5px solid ${B.borderStrong}`, borderRadius: 9, padding: '9px 15px', display: 'inline-flex', alignItems: 'center', gap: 6 },
    expBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14 },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
  };

  const Arrow = ({ dir, label, dim }) => (
    <div style={{ ...s.arrowText, color: dim ? B.faint : B.text }}>
      {dir === 'right' ? `${label} →` : `← ${label}`}
    </div>
  );

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>Flow-based Reversibility</h1>
        <span style={s.badge}>Case Study</span>
        <span style={s.lvlBadge}>HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Schematic: the reversible mapping ──────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>A flow-based model: one reversible mapping</div>
          <div style={s.schematic}>
            <div style={s.endBox}>
              <MiniGrid data={NOISE} />
              <div style={s.endLabel}>Latent noise</div>
            </div>
            <div style={s.arrows}>
              <Arrow dir="right" label="forward" />
              <Arrow dir="left" label="reverse" />
            </div>
            <div style={s.endBox}>
              <MiniGrid data={IMAGE} />
              <div style={s.endLabel}>Image</div>
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.8rem', lineHeight: 1.55, textAlign: 'center' }}>
            A <strong>flow-based model</strong> is a single <strong>reversible</strong> mapping: run it
            forward to turn noise into an image, or backward to turn the image back into the same noise.
          </p>
        </div>

        {/* ── Interactive stage ──────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.stage}>
            <div style={{ flex: '1 1 260px', minWidth: 240 }}>
              <div style={s.sectionLabel}>Current state</div>
              <canvas ref={bigRef} style={s.bigCanvas} role="img"
                aria-label={`Current state: ${state === 'noise' ? 'latent noise' : 'the image'}. ${lossless ? 'It matches the exact target with zero differences.' : `${diff} of ${CELLS} cells differ from the exact target.`}`} />
              <div style={{ marginTop: 8 }}>
                <span style={s.stateBadge(state)}>{state === 'noise' ? 'NOISE' : 'IMAGE'}</span>
              </div>
            </div>

            <div style={{ flex: '1 1 240px', minWidth: 220 }}>
              <div style={s.sectionLabel}>Run the model</div>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button style={s.btnPrimary(state === 'noise')} onClick={forward} disabled={state !== 'noise'}
                  aria-label="Forward: noise to image">
                  Forward: noise to image
                </button>
                <button style={s.btnPrimary(state === 'image')} onClick={reverse} disabled={state !== 'image'}
                  aria-label="Reverse: image to noise">
                  Reverse: image to noise
                </button>
                <button style={s.btnGhost} onClick={reset} aria-label="Reset to starting noise">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 8a5 5 0 1 1 1.5 3.5" /><polyline points="3 14 3 11 6 11" />
                  </svg>
                  Reset
                </button>
              </div>

              {/* Integrity / no-loss readout */}
              <div style={{
                marginTop: '1rem', padding: '0.8rem 0.95rem', borderRadius: 10,
                border: `1px solid ${B.border}`, borderLeft: `4px solid ${lossless ? B.greenDark : B.red}`,
                background: lossless ? B.greenSoft : B.redSoft,
              }} role="status" aria-live="polite">
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {lossless ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={B.greenDark} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4" /></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={B.red} strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><line x1="4" y1="4" x2="12" y2="12" /><line x1="12" y1="4" x2="4" y2="12" /></svg>
                  )}
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: lossless ? B.greenDark : B.red }}>
                    {lossless ? 'Lossless: exact match' : 'Information lost (lossy)'}
                  </span>
                </div>
                <div style={{ fontSize: '0.8rem', color: B.muted, marginTop: 4, fontFamily: mono }}>
                  {diff} of {CELLS} cells differ from the exact {state === 'noise' ? 'noise' : 'image'}
                </div>
              </div>

              {/* Lossy contrast toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.9rem', cursor: 'pointer', fontSize: '0.8rem', color: B.text }}>
                <input type="checkbox" checked={lossy} onChange={toggleLossy} style={{ accentColor: B.red, width: 16, height: 16 }} />
                Simulate a <strong>lossy</strong> forward step (for contrast)
              </label>
            </div>
          </div>

          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '1rem', lineHeight: 1.55 }}>
            <strong>Try it:</strong> press <strong>Forward</strong> to turn the noise into the image, then{' '}
            <strong>Reverse</strong> to go back. In the default (flow-based) mode the reverse returns to the{' '}
            <strong>exact</strong> starting noise: <strong>0 of {CELLS}</strong> cells differ, so it is{' '}
            <strong>lossless</strong>. Tick the lossy option to see a step that throws information away instead.
          </p>
        </div>

        {/* ── Explainer accordion ────────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen((o) => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              Reversible and lossless, in brief
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                A <strong>flow-based model</strong> learns one mapping that works in both directions. Because
                every step is <strong>reversible</strong>, running it forward and then backward returns the
                <strong> exact</strong> starting point. No information is discarded, so it is{' '}
                <strong>lossless</strong>.
              </p>
              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Reversible</div>
                  <ul style={s.expList}>
                    <li>Forward: noise becomes the image.</li>
                    <li>Reverse: the image becomes the same noise.</li>
                    <li>Every step has an <strong>exact inverse</strong>.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Lossless</div>
                  <ul style={s.expList}>
                    <li>The round trip differs by <strong>0 cells</strong>.</li>
                    <li>Nothing is rounded off or thrown away.</li>
                    <li>A <strong>lossy</strong> step (the toggle) breaks this.</li>
                  </ul>
                </div>
              </div>
              <p style={s.expNote}>
                This differs from models that only go one way or that lose detail. A flow-based model keeps a
                perfect two-way link between the noise and the image.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
